import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {ICoreUniverseEnv} from "/@lib@/core/universe.js";
import {EUserType, JUser, USER, UsersSrv} from "/@lib@/core/user.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";

/**
 * Widget affichant une ref à un user.
 * Evolutions potentielles:
 * - ajout de l'avatar en icone
 * - ajout d'un tooltip des présentation détaillée, et fonctions de communication avec ce user
 * - ajout listeners sur update du user lorsque UsersSrv implémentera cette possibilité
 *
 * Class :
 * 	- inline : passe en inline-flex.
 * 	- list : grid, ...
 */
export interface UserRef extends BaseElement {
	initialize(init: OUserRefInit): this
}

export interface OUserRefInit extends OSkinableInit {
	reg?: IReg<ICoreUniverseEnv>
	nickOrAccount?: string
	user?: JUser
	/** true par défaut */
	withTitle?: boolean
	/** true par défaut */
	withIcon?: boolean,
	/** false par défaut */
	showLongName?: boolean
}

export class UserRef extends BaseElement {

	reg: IReg<ICoreUniverseEnv>;

	protected _nickOrAccount: string;

	protected _user?: JUser;

	protected _props: OUserRefInit;

	get nickOrAccount() {return this._nickOrAccount}

	set nickOrAccount(n: string) {
		if (this._nickOrAccount !== n) {
			this._nickOrAccount = n;
			this._user = null;
			if (n) this.fetchUser();
			this.refresh();
		}
	}

	get user() {return this._user}

	set user(n: JUser) {
		this._nickOrAccount = n ? n.account : null;
		this._user = n;
		this.refresh();
	}

	get usersSrv(): UsersSrv {return this.reg.env.universe.useUsers}

	protected _initialize(init: OUserRefInit) {
		this._props = Object.assign({withIcon: true, withTitle: true, showLongName: false}, init);
		this.reg = this.findReg(this._props);
		this._attach(this.localName, this._props, <span/>);
		if (this._props.user) {
			this.user = this._props.user;
		} else {
			this.nickOrAccount = this._props.nickOrAccount;
		}
	}

	buildInitFromAtts(init?: OUserRefInit): OUserRefInit {
		init = super.buildInitFromAtts(init);
		init.nickOrAccount = this.getAttribute('account');
		return init;
	}

	// connectedCallback() {
	// 	super.connectedCallback();
	// 	//TODO listen user updates auprès de this.usersSrv
	// }
	// disconnectedCallback() {
	// 	//TODO unlisten user updates auprès de this.usersSrv
	// }


	protected _pending: Promise<JUser>;

	async fetchUser() {
		const pending = this._pending = this.usersSrv.getUserBatch(this._nickOrAccount);
		const user = await pending;
		if (this._pending !== pending) return; //race cond
		this._pending = null;
		this._user = user;
		this.refresh();
	}

	protected _refresh() {
		const span = this.shadowRoot.lastElementChild;
		DOM.setAttrBool(this, "data-withIcon", this._props.withIcon);
		if (this._pending) {
			DOM.setTextContent(span, "...");
			if (this._props.withTitle) DOM.setAttr(this, "title", null);
		} else if (!this.nickOrAccount) {
			DOM.setTextContent(span, "");
			DOM.setAttr(this, "title", null);
			DOM.setAttrBool(this, "data-isEmpty", true);
		} else if (!this._user) {
			DOM.setTextContent(span, this._nickOrAccount);
			DOM.setAttr(this, "title", "Utilisateur / groupe inconnu");
			DOM.setAttrBool(this, "data-isGroup", false);
			DOM.setAttrBool(this, "data-isEmpty", false);
			DOM.setAttrBool(this, "data-isUnknown", true);
		} else {
			const longName = USER.getLongName(this._user);
			let text = USER.getPrimaryName(this._user);
			if (this._props.showLongName && longName) text += ` (${longName})`;
			DOM.setTextContent(span, text);
			if (this._user.isUnknown)
				DOM.setAttr(this, "title", "Utilisateur / groupe inconnu");
			else
				DOM.setAttr(this, "title", this._props.withTitle ? longName : null);
			DOM.setAttrBool(this, "data-isGroup", this._user.userType === EUserType.group);
			DOM.setAttrBool(this, "data-isEmpty", false);
			DOM.setAttrBool(this, "data-isUnknown", this._user.isUnknown);
			DOM.setAttrBool(this, "data-isAnonymous", (this._user as JUser).isAnonymous);
		}
	}

}

REG.reg.registerSkin('c-userref', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 1.2em;
		min-width: 0;
		color: var(--alt2-color);
	}

	:host([data-isEmpty]) {
		visibility: collapse;
	}

	:host(.inline) {
		display: inline;
	}

	:host(.list) {
		color: var(--color);
	}

	:host([data-withIcon]) span {
		background-image: url(/@skin@/core/objects/user.svg);
		background-position: left;
		background-size: auto 1em;
		background-repeat: no-repeat;
		padding-inline-start: 1.2em;
	}

  :host([data-isGroup][data-withIcon]) span {
	  background-image: url(/@skin@/core/objects/group.svg);
	  padding-inline-start: 1.6em;
  }

  :host([data-isUnknown]) span {
	  filter: grayscale(100%);
	  font-style: italic;
  }

  :host([data-isAnonymous]) span {
	  background-image: unset;
	  font-style: italic;
  }


`);

customElements.define('c-userref', UserRef);